/**
 * Administrator area.
 *
 * The route exists and is gated by `RequireAdministrator`, so the authorisation path is real and testable. It is the *index* of
 * the administration screens rather than a screen of its own: the account directory, the moderation queue and the template
 * catalogue each have an address, and this page links to them and carries the settings that are small enough to edit in
 * place — the Forge price table, the provider connections, the prompt patterns, the public handles and the categories.
 *
 * The account-gap placeholder of earlier releases is withdrawn (TASK-0061): the API publishes the account directory and its
 * screens exist, so a sentence saying it does not would now be a lie on the first page an operator reads.
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/surface';
import { ProviderProfileAdmin } from '@/features/admin/components/ProviderProfileAdmin';
import { CategoryAdmin } from '@/features/admin/components/CategoryAdmin';
import { PromptPatternAdmin } from '@/features/admin/components/PromptPatternAdmin';
import { PublicIdentifierAdmin } from '@/features/admin/components/PublicIdentifierAdmin';
import { ForgePricingEditor } from '@/features/forge/components/ForgePricingEditor';

/** Where the template administration that already ships lives. */
const TEMPLATE_CATALOGUE_ROUTE = '/templates';

/** The moderator's queue (TASK-0050). */
const PUBLICATION_REVIEW_ROUTE = '/admin/publication-review';

/** The account directory (TASK-0061). */
const ADMIN_USERS_ROUTE = '/admin/users';

/** The registration policy (TASK-0062). */
const ADMIN_AUTH_POLICY_ROUTE = '/admin/auth-policy';

/** Administrator area. */
export function AdminPage() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('nav.admin')} description={t('admin.subtitle')} />

      <Card>
        <CardHeader>
          <CardTitle>{t('admin.statusTitle')}</CardTitle>
          <CardDescription>{t('common.comingSoon')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <p className="text-sm text-muted-foreground">{t('admin.usersHint')}</p>
          <Link className="text-sm text-primary hover:underline" to={ADMIN_USERS_ROUTE}>
            {t('admin.usersAction')}
          </Link>
          <p className="text-sm text-muted-foreground">
            {t('admin.templatesHint')}{' '}
            <Link className="text-primary hover:underline" to={TEMPLATE_CATALOGUE_ROUTE}>
              {t('admin.templatesAction')}
            </Link>
          </p>
        </CardContent>
      </Card>

      {/* The registration policy (TASK-0062), on the index beside the account directory: it is the system-wide counterpart of
          the per-account verification card, and the one screen that decides what every new registration must prove. It is
          linked from the sidebar as well; the card is here so the index is not the one place an operator would have to leave
          to discover it. */}
      <Card>
        <CardHeader>
          <CardTitle>{t('admin.authPolicy.title')}</CardTitle>
          <CardDescription>{t('admin.authPolicy.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <p className="text-sm text-muted-foreground">{t('admin.authPolicy.formDescription')}</p>
          <Link className="text-sm text-primary hover:underline" to={ADMIN_AUTH_POLICY_ROUTE}>
            {t('nav.adminAuthPolicy')}
          </Link>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('admin.publicationReview.title')}</CardTitle>
          <CardDescription>{t('admin.publicationReview.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <p className="text-sm text-muted-foreground">{t('admin.publicationReview.hint')}</p>
          {/* A link rather than the queue itself: the queue is paged and long, and rendering it on the administration
              landing page would make every visit to the settings pay for a read of the moderation queue. */}
          <Link className="text-sm text-primary hover:underline" to={PUBLICATION_REVIEW_ROUTE}>
            {t('admin.publicationReview.decision.title')}
          </Link>
        </CardContent>
      </Card>

      {/* The one administrator setting the API exposes today, so it sits on the real screen rather than behind a
          placeholder: an operator who changes a price expects the change to be visible where they made it. */}
      <ForgePricingEditor />

      {/* The platform connections the price table and every user's picker reference (ADR-0019). It sits above the price
          table in reading order, because a price row can only name a profile that exists here. */}
      <ProviderProfileAdmin />

      {/* The prompt-pattern catalog the framework wizard offers (TASK-0039). It is the fourth axis of the product, so it
          is authored here beside the provider connections rather than in code. */}
      <PromptPatternAdmin />

      {/* The public handles an account is known by and the ones nobody may take (TASK-0060). It sits above the category
          rail because it is small — one lookup, one rename, one list — and belongs with the account-administration
          questions an operator asks first. */}
      <PublicIdentifierAdmin />

      {/* The category rail a prompt is filed under (TASK-0052). It sits last because it is the largest panel: two levels
          of rows, one expanded at a time. */}
      <CategoryAdmin />
    </div>
  );
}
